<template>
  <portal
    v-if="!!$slots.toolbarBefore"
    to="toolbarBefore"
  >
    <slot name="toolbarBefore" />
  </portal>
  <portal
    v-if="!!$slots.toolbar"
    to="toolbar"
  >
    <slot name="toolbar" />
  </portal>
  <portal
    v-if="!!$slots.header"
    to="header"
  >
    <slot name="header" />
  </portal>
  <portal
    v-if="!!$slots.layout"
    to="layout"
  >
    <slot name="layout" />
  </portal>

  <portal
    v-if="!!$slots.footer"
    to="footer"
  >
    <slot name="footer" />
  </portal>

  <!-- Expanded toolbar slots -->
  <portal
    v-if="!!$slots['toolbar-ll']"
    to="toolbar-ll"
  >
    <slot name="toolbar-ll" />
  </portal>
  <portal
    v-if="!!$slots['toolbar-lr']"
    to="toolbar-lr"
  >
    <slot name="toolbar-lr" />
  </portal>
  <portal
    v-if="!!$slots['toolbar-expanded']"
    to="toolbar-expanded"
  >
    <slot name="toolbar-expanded" />
  </portal>
  <portal
    v-if="!!$slots['toolbar-rl']"
    to="toolbar-rl"
  >
    <slot name="toolbar-rl" />
  </portal>
  <portal
    v-if="!!$slots['toolbar-rr']"
    to="toolbar-rr"
  >
    <slot name="toolbar-rr" />
  </portal>

  <q-page
    v-resize:throttle.500="onContainerResize"
    v-bind="$attrs"
  >
    <slot />
  </q-page>
</template>

<script>
import { Dark } from 'quasar'

import resize from 'vue3-resize-directive'

/**
 * PageLayout scaffolds the layout of the page
 *
 * SLOTS:
 * - default: content to display inside the page content section
 * - toolbar: content to append ta the top right of the main page toolbar
 * - toolbarBefore: content to prepend ta the top left of the main page toolbar (between hamburger and title)
 * - header: content to append underneath the main page toolbar, but remain stuck inside the header
 * - layout: content to append to the main parent QLayout
 *
 * Expanded toolbar SLOTS:
 * - toolbar-[ll, lr, expanded, rl, rr]
 *
 * EVENTS:
 * - scroll: emit when scroll position is changed
 * - resize: emit when the page container size changes
 */
export default {
  directives: {
    resize
  },
  inheritAttrs: false,
  props: {
    // QLayout view (check Quasar docs for more)
    view: {
      type: String,
      default: 'lHh LpR lFr'
    },
    // If slot is not defined, you can define a string to use as the page title. Default is route title.
    // You may also use object syntax to define title and subtitle strings
    title: {
      type: [String, Object],
      default: null
    },
    // Color of header area
    color: {
      type: String,
      default: Dark.isActive ? 'blue-grey-8' : 'blue-8'
    },
    // Text color of header area content
    textColor: {
      type: String,
      default: 'white'
    },
    // Shadow behind header
    shadow: {
      type: Number,
      default: 4
    },
    // Display loading spinner if some content is loading
    loading: {
      type: Boolean,
      default: false
    },
    // Enable light or dark mode on the main navigation sidebar
    dark: {
      type: Boolean,
      default: true
    },
    // Simple sidebar - remove "hardcoded" elements
    simpleSidebar: {
      type: Boolean,
      default: false
    },
    // Enable expanded toolbar mode for morphing content from the main toolbar onto a secondary bar for smaller screens
    // Useful when the main toolbar has a lot of settings which won't fit on small screens
    expandedToolbar: {
      type: Boolean,
      default: false
    },
    // Force the drawer to remain open when clicking an item
    // Useful for views which uses its own drawer slot override and keeping the drawer open once leaving that view
    persistentDrawer: {
      type: Boolean,
      default: false
    },
    // Disable all page layout elements
    // Only visible slot will be the QPageContainer
    hidden: {
      type: Boolean,
      default: false
    }
  },
  emits: ['scroll', 'resize'],
  data () {
    return {
      layout: null
    }
  },
  computed: {
    layoutOptions () {
      const options = {}
      for (const ele in this.$props) {
        options[ele] = this.$props[ele]
      }
      return options
    }
  },
  watch: {
    'layout.scroll' (pos) {
      this.$emit('scroll', pos)
    },
    'layoutOptions' (val) {
      this.layout.options = val
    }
  },
  methods: {
    onContainerResize (e) {
      this.$emit('resize', {
        width: e.offsetWidth,
        height: e.offsetHeight
      })
    },
    storeLayout (val) {
      this.layout = val
      this.$nextTick(() => {
        this.layout.options = this.layoutOptions
      })
    }
  },
  created () {
    this.$bus.emit('layout:get', this.storeLayout)
  },
  beforeUnmount () {
    this.layout.resetLayout()
  }
}
</script>
